import axios from "axios";
import { firebaseAuth } from "../firebaseConfig";
import { serializeOrderStop } from "./orderPayload";
import {
  IWorkspaceBase,
  IWorkspaceAccessBase,
  IOrder,
  IInvoice,
  IPaginatedResult,
  Timestamp,
  IRouteItem,
  IOrderProduct,
  IContactPerson,
} from "../types";

export const signup = async (token: string) => {
  const response = await apiPost(token, "/api/signup/");
  return response.data;
};

export const createUser = async (
  token: string,
  workspaceId: string,
  user: IWorkspaceAccessBase,
) => {
  const response = await apiPost(
    token,
    `/api/workspaces/${workspaceId}/invite`,
    {
      email: user.email,
      name: user.name,
      role: user.role
    }
  );
  return response.data;
};

export const getMyUser = async (token: string) => {
  const response = await apiGet(token, "/api/users/me");
  return response.data;
};

export const createWorkspace = async (
  token: string,
  workspace: IWorkspaceBase,
) => {
  const response = await apiPost(token, "/api/workspaces/", workspace);
  return response.data;
};

export const updateWorkspace = async (
  token: string,
  workspaceId: string,
  workspace: IWorkspaceBase,
) => {
  const response = await apiPut(
    token,
    `/api/workspaces/${workspaceId}`,
    workspace,
  );
  return response.data;
};

export const deleteWorkspace = async (token: string, workspaceId: string) => {
  const response = await apiDelete(token, `/api/workspaces/${workspaceId}`);
  return response.data;
};

export const markWorkspaceActivity = async (
  token: string,
  workspaceId: string,
) => {
  const response = await apiPost(
    token,
    `/api/workspaces/${workspaceId}/activity`,
    {},
    workspaceId,
  );
  return response.data;
};

export const deleteOrderApi = async (
  token: string,
  workspaceId: string,
  orderId: string
) => {
  const response = await apiDelete(token, `/api/orders/${orderId}`, workspaceId);
  return response.data;
};

export const checkPricing = async (
  token: string,
  workspaceId: string,
  order: IOrder,
  isCompanyVatRegistered: boolean
) => {
  // Helper to safely get nested properties or return a default
  const safeGet = (obj: any, path: string, defaultValue: any = null) => 
    path.split('.').reduce((acc, part) => acc && acc[part] !== undefined && acc[part] !== null ? acc[part] : defaultValue, obj);

  // Helper to ensure numeric values
  const ensureNumber = (value: any, defaultValue = 0): number => {
    // Check if value is already a valid number
    if (typeof value === 'number' && !isNaN(value)) {
      return value;
    }
    // Try to convert to number if it's a string
    if (typeof value === 'string') {
      const parsed = parseFloat(value);
      return isNaN(parsed) ? defaultValue : parsed;
    }
    // Return default for null, undefined, or invalid values
    return defaultValue;
  };

  // Helper to ensure string values (converts arrays or other types to strings)
  const ensureString = (value: any, defaultValue = ""): string => {
    if (typeof value === 'string') {
      return value;
    }
    // If it's an array with at least one element, take the first element as string
    if (Array.isArray(value) && value.length > 0) {
      return String(value[0]);
    }
    // For other types, try to convert if possible, otherwise use default
    return value !== null && value !== undefined ? String(value) : defaultValue;
  };

  // Helper to ensure valid coordinates (at least one non-zero value)
  const ensureValidCoordinates = (coord: any) => {
    const lat = ensureNumber(coord?.lat);
    const lon = ensureNumber(coord?.lon);
    
    // If both coordinates are 0, return null values instead
    // This prevents backend from trying to calculate with potentially invalid coordinates
    if (lat === 0 && lon === 0) {
      return { lat: null, lon: null };
    }
    
    return { lat, lon };
  };

  try {
    // Ensure order.cargo is an array
    const safeCargoArray = Array.isArray(order?.cargo) ? order.cargo : [];

    // Construct the payload strictly according to the POST /api/orders/pricing schema
    const payload = {
      // Top-level fields from schema
      cargo: safeCargoArray.map((item: IOrderProduct) => {
        // Ensure item exists
        if (!item) {
          return {
            description: "",
            descriptionShort: "",
            height: 0,
            imgUrls: [],
            itinerary: {
              deliveryStopNumber: 0,
              pickupStopNumber: 0
            },
            length: 0,
            price: 0,
            pricing: {
              priceModel: [],
              pricePerCargoUnit: 0,
              pricePerDistanceUnit: 0,
              pricePerVolumeUnit: 0,
              pricePerWeightUnit: 0,
              vat: 25 // Default VAT value
            },
            productId: "",
            productType: [],
            quantity: 1,
            weight: 0,
            width: 0
          };
        }

        // Full default pricing object with all required fields
        const defaultPricing = {
          priceModel: [] as string[],
          pricePerCargoUnit: 0,
          pricePerDistanceUnit: 0,
          pricePerVolumeUnit: 0,
          pricePerWeightUnit: 0,
          // Default VAT based on registration status
          vat: isCompanyVatRegistered ? 25 : 0 
        };

        // Make sure pricing exists as an object
        const itemPricing = typeof item.pricing === 'object' && item.pricing !== null ? item.pricing : {};
        
        // Determine the VAT for this specific item, respecting the company registration status
        const currentItemVat = isCompanyVatRegistered 
            ? ensureNumber(itemPricing && 'vat' in itemPricing ? (itemPricing as any).vat : undefined, 25) 
            : 0; // Force VAT to 0 if company is not registered

        // Full pricing object with defaults applied for missing fields
        const pricingWithDefaults = {
          ...defaultPricing,
          ...itemPricing,
          // Use the calculated VAT based on registration status
          vat: currentItemVat,
          // Ensure priceModel is an array
          priceModel: Array.isArray(itemPricing && 'priceModel' in itemPricing ? (itemPricing as any).priceModel : undefined) 
            ? (itemPricing as any).priceModel 
            : (typeof (itemPricing as any).priceModel === 'string' ? [(itemPricing as any).priceModel] : defaultPricing.priceModel),
          // Ensure all pricing numbers have defaults
          pricePerCargoUnit: ensureNumber(itemPricing && 'pricePerCargoUnit' in itemPricing ? (itemPricing as any).pricePerCargoUnit : undefined, 0),
          pricePerDistanceUnit: ensureNumber(itemPricing && 'pricePerDistanceUnit' in itemPricing ? (itemPricing as any).pricePerDistanceUnit : undefined, 0),
          pricePerVolumeUnit: ensureNumber(itemPricing && 'pricePerVolumeUnit' in itemPricing ? (itemPricing as any).pricePerVolumeUnit : undefined, 0),
          pricePerWeightUnit: ensureNumber(itemPricing && 'pricePerWeightUnit' in itemPricing ? (itemPricing as any).pricePerWeightUnit : undefined, 0),
          pricePerTimeUnit: ensureNumber(itemPricing && 'pricePerTimeUnit' in itemPricing ? (itemPricing as any).pricePerTimeUnit : undefined, 0),
          timePeriodInMinutes: ensureNumber(itemPricing && 'timePeriodInMinutes' in itemPricing ? (itemPricing as any).timePeriodInMinutes : undefined, 0)
        };

        return {
          seatsEnabled: item.seatsEnabled ?? false, seatCount: item.seatCount,
          description: item?.description || "",
          descriptionShort: item?.descriptionShort || "", 
          height: ensureNumber(item?.height, 0),
          imgUrls: Array.isArray(item?.imgUrls) ? item.imgUrls : [], 
          itinerary: { 
            deliveryStopNumber: ensureNumber(safeGet(item, 'itinerary.deliveryStopNumber', 0)), 
            pickupStopNumber: ensureNumber(safeGet(item, 'itinerary.pickupStopNumber', 0))
          },
          length: ensureNumber(item?.length, 0),
          price: ensureNumber(item?.price, 0), 
          pricing: pricingWithDefaults,
          productId: item?.productId || "",
          productType: Array.isArray(item?.productType) ? item.productType : [],
          quantity: ensureNumber(item?.quantity, 1),
          weight: ensureNumber(item?.weight, 0),
          width: ensureNumber(item?.width, 0),
        };
      }),
      comment: order?.comment || "",
      customer: { 
        contactEmail: safeGet(order, 'customer.contactEmail', ""),
        firstName: ensureString(safeGet(order, 'customer.firstName', "")),
        invoiceEmail: safeGet(order, 'customer.invoiceEmail', ""),
        isCompany: !!safeGet(order, 'customer.isCompany', false),
        lastName: ensureString(safeGet(order, 'customer.lastName', "")),
        name: safeGet(order, 'customer.name', ""),
        organizationNumber: safeGet(order, 'customer.organizationNumber', ""),
        phoneNumber: safeGet(order, 'customer.phoneNumber', ""),
        primaryAddress: { 
          addressLine: safeGet(order, 'customer.primaryAddress.addressLine', ""),
          careOf: safeGet(order, 'customer.primaryAddress.careOf', ""),
          city: safeGet(order, 'customer.primaryAddress.city', ""),
          coordinate: ensureValidCoordinates(safeGet(order, 'customer.primaryAddress.coordinate', {})),
          countryCode: safeGet(order, 'customer.primaryAddress.countryCode', ""),
          placeId: safeGet(order, 'customer.primaryAddress.placeId', ""),
          postCode: safeGet(order, 'customer.primaryAddress.postCode', "")
        },
        socialSecurityNumber: safeGet(order, 'customer.socialSecurityNumber', "")
      },
      customerId: order?.customerId || "",
      driverId: order?.driverId || "",
      orderPricing: order?.orderPricing || undefined,
      route: (order?.route || []).map((route: IRouteItem) => {
        const routeItem = {
          contact: Array.isArray(route?.contact) 
            ? route.contact.map((c: IContactPerson) => ({ name: c?.name || "", phoneNumber: c?.phoneNumber || ""})) 
            : [],
          deliveryComment: safeGet(route, 'deliveryComment', ""), 
          location: { 
            addressLine: safeGet(route, 'location.addressLine', ""),
            careOf: safeGet(route, 'location.careOf', ""),
            city: safeGet(route, 'location.city', ""),
            coordinate: ensureValidCoordinates(safeGet(route, 'location.coordinate', {})),
            countryCode: safeGet(route, 'location.countryCode', ""),
            id: safeGet(route, 'location.id', ""),
            placeId: safeGet(route, 'location.placeId', ""),
            postCode: safeGet(route, 'location.postCode', "")
          },
          stopDate: route?.stopDate ? Timestamp.toFormattedDate(route.stopDate, 'YYYY-MM-DD') : null,
          stopNumber: ensureNumber(route?.stopNumber, 0),
          timing: { 
            after: safeGet(route, 'timing.after', ""),
            before: safeGet(route, 'timing.before', ""),
            earliest: !!safeGet(route, 'timing.earliest', false),
            latest: !!safeGet(route, 'timing.latest', false)
          },
          requiredTimeWindows: route.requiredTimeWindows ?? null,
          preferredTimeWindows: route.preferredTimeWindows ?? null
        };
        return serializeOrderStop(routeItem);
      })
    };

    // Use the existing robust apiPost helper
    const response = await apiPost(token, "/api/orders/pricing", payload, workspaceId);
    
    // Return the full response data (InvoicePricingResponse)
    return response; 

  } catch (error) {
    console.error("Error constructing payload or calling API in checkPricing:", error);
    // Return structure expected by OrderSummary
    return { data: null, error: error instanceof Error ? error : new Error('Unknown pricing check error'), success: false }; 
  }
};

export const createOrder = async (
  token: string,
  workspaceId: string,
  // Accept a more flexible order payload type, reflecting what's actually sent
  orderPayload: Omit<IOrder, 'route'> & { 
    route: Array<Omit<IRouteItem, 'stopDate'> & { stopDate: string | null }>; 
  }
) => {
  try {
    const response = await apiPost(token, "/api/orders/", {
      ...orderPayload,
      route: orderPayload.route.map(serializeOrderStop),
    }, workspaceId);
    return response; // apiPost returns data or throws error
    } catch (error: any) {
      // Extract structured error from response if available
      const structuredError = error?.data?.response?.data?.error || error;
      return { 
        success: false,
        error: structuredError
      }; 
    }
};

export const updateOrderDraft = async (
  token: string,
  workspaceId: string,
  orderId: string,
  orderPayload: Omit<IOrder, 'route'> & {
    route: Array<Omit<IRouteItem, 'stopDate'> & { stopDate: string | null }>;
  }
) => {
  const response = await apiPut(
    token,
    `/api/orders/${orderId}/draft`,
    { ...orderPayload, route: orderPayload.route.map(serializeOrderStop) },
    workspaceId
  );
  return response.data;
};

export const cancelOrder = async (
  token: string,
  workspaceId: string,
  id: string,
) => {
  const response = await apiPost(
    token,
    `/api/orders/${id}/cancel`,
    {},
    workspaceId,
  );
  return response.data;
};

export const invoiceOrder = async (
  token: string,
  id: string,
) => {
  const response = await apiPost(
    token,
    `/api/orders/${id}/invoice`
  );
  return response.data;
};

export const getOrders = async (
  token: string,
  workspaceId: string,
): Promise<IOrder[]> => {
  const envelope = await apiGet(token, `/api/orders/`, workspaceId);
  return (envelope?.data as IOrder[]) ?? [];
};

export interface OrdersPageParams {
  limit?: number;
  cursor?: string;
  statuses?: string[];
  driverId?: string;
  search?: string;
}

/**
 * Fetches a single cursor page of orders from the API, with optional
 * server-side status/driver filters and prefix search.
 */
export const getOrdersPaginated = async (
  token: string,
  workspaceId: string,
  params: OrdersPageParams = {},
): Promise<IPaginatedResult<IOrder>> => {
  const query = new URLSearchParams();
  query.set("limit", String(params.limit ?? 20));
  if (params.cursor) query.set("cursor", params.cursor);
  (params.statuses ?? []).forEach((status) => query.append("status", status));
  if (params.driverId) query.set("driverId", params.driverId);
  if (params.search) query.set("search", params.search);

  const envelope = await apiGet(
    token,
    `/api/orders/?${query.toString()}`,
    workspaceId,
  );
  const data = envelope?.data ?? {};
  return {
    items: (data.orders as IOrder[]) ?? [],
    pagination: data.pagination,
  };
};

export const assignOrder = async (
  token: string,
  workspaceId: string,
  orderId: string,
  driverId: string
) => {
  const response = await apiPost(
    token,
    `/api/orders/${orderId}/assign/${driverId}`,
    undefined,
    workspaceId
  );
  return response.data;
};

export const completeOrder = async (
  token: string,
  workspaceId: string,
  orderId: string
) => {
  const response = await apiPost(
    token,
    `/api/orders/${orderId}/complete`,
    { cargos: [] },  // Add empty cargos array since we're not tracking individual cargo receipts
    workspaceId
  );
  return response.data;
};

export const startOrder = async (
  token: string,
  workspaceId: string,
  orderId: string
) => {
  const response = await apiPost(
    token,
    `/api/orders/${orderId}/start`,
    {},
    workspaceId
  );
  return response.data;
};

export const confirmOrder = async (
  token: string,
  workspaceId: string,
  orderId: string,
  driverId: string
) => {
  // First assign the driver to the order
  await assignOrder(token, workspaceId, orderId, driverId);
  
  // Then confirm the order
  const response = await apiPost(
    token,
    `/api/orders/${orderId}/confirm`,
    {},
    workspaceId
  );
  return response.data;
};

export const getDriverPositionByEmail = async (
  token: string,
  workspaceId: string,
  email: string,
) => {
  const response = await apiPost(
    token,
    `/api/drivers/${email}/position`,
    undefined,
    workspaceId,
  );
  return response.data;
};

export const getDriverRoutes = async (
  token: string,
  workspaceId: string,
  driverId: string,
  stopDate: string
) => {
  const response = await apiGet(
    token,
    `/api/routes/driver/${driverId}/date/${stopDate}`,
    workspaceId
  );
  return response.data;
};

export const getDriverSchedule = async (
  token: string,
  workspaceId: string,
  driverId: string,
  stopDate: string
) => {
  const response = await apiGet(
    token,
    `/api/routes/driver/${driverId}/date/${stopDate}/schedule`,
    workspaceId
  );
  return response.data;
};

export const updateDriverSchedule = async (
  token: string,
  workspaceId: string,
  driverId: string,
  stopDate: string,
  schedule: any
) => {
  const response = await apiPut(
    token,
    `/api/routes/driver/${driverId}/date/${stopDate}/schedule`,
    schedule,
    workspaceId
  );
  return response.data;
};

export const getRouteOptimizationCapability = async (
  token: string,
  workspaceId: string,
) => {
  const response = await apiGet(
    token,
    "/api/routes/optimization/capability",
    workspaceId,
  );
  return response.data?.capability;
};

export const previewOptimizedDriverRoute = async (
  token: string,
  workspaceId: string,
  driverId: string,
  stopDate: string,
  serviceDurationSeconds = 600,
) => {
  const response = await apiPost(
    token,
    `/api/routes/driver/${encodeURIComponent(driverId)}/date/${stopDate}/optimize`,
    { serviceDurationSeconds },
    workspaceId,
  );
  return response.data?.optimization;
};

export const rescheduleRouteItem = async (
  token: string,
  workspaceId: string,
  routeId: string,
  data: {
    status: string;
    routeDate: string;
    routeStopNumber: number;
  }
) => {
  const response = await apiPut(
    token,
    `/api/routes/${routeId}/reschedule`,
    data,
    workspaceId
  );
  return response.data;
};

// Invoice related functions
export const getNextInvoiceNumber = async (token: string, companyId: string) => {
  const response = await apiGet(token, `/api/invoices/company/${companyId}/next`);
  return response.data;
};

export const getInvoices = async (token: string, workspaceId: string) => {
  const response = await apiGet(token, `/api/invoices/`, workspaceId);
  return response.data;
};

/**
 * Fetches a single cursor page of invoices from the API.
 */
export const getInvoicesPaginated = async (
  token: string,
  workspaceId: string,
  params: { limit?: number; cursor?: string } = {},
): Promise<IPaginatedResult<IInvoice>> => {
  const query = new URLSearchParams();
  query.set("limit", String(params.limit ?? 20));
  if (params.cursor) query.set("cursor", params.cursor);

  const envelope = await apiGet(
    token,
    `/api/invoices/?${query.toString()}`,
    workspaceId,
  );
  const data = envelope?.data ?? {};
  return {
    items: (data.invoices as IInvoice[]) ?? [],
    pagination: data.pagination,
  };
};

export const createInvoice = async (token: string, workspaceId: string, invoice: any) => {
  const response = await apiPost(token, `/api/invoices`, invoice, workspaceId);
  return response.data;
};

export const createCreditNote = async (token: string, workspaceId: string, invoice: any) => {
  const response = await apiPost(token, `/api/invoices/creditnote`, invoice, workspaceId);
  return response.data;
};

export const getInvoice = async (token: string, invoiceId: string) => {
  const response = await apiGet(token, `/api/invoices/${invoiceId}`);
  return response.data;
};

export const updateInvoice = async (token: string, invoiceId: string, invoice: any) => {
  const doRequest = async (tkn: string) => {
    const headers: any = { Authorization: `Bearer ${tkn}` };
    if (invoice.workspaceId) headers["X-Workspace-Id"] = invoice.workspaceId;
    const response = await axios.put(`${baseUrl}/api/invoices/${invoiceId}`, invoice, { headers });
    return response.data;
  };
  try {
    return await doRequest(token);
  } catch (error: any) {
    if (error?.response?.status === 401 && firebaseAuth.currentUser) {
      const fresh = await firebaseAuth.currentUser.getIdToken(true);
      return await doRequest(fresh);
    }
    console.error(`Error updating invoice:`, error);
    throw error;
  }
};

export const bookInvoice = async (token: string, invoiceId: string) => {
  const response = await apiPost(token, `/api/invoices/${invoiceId}/book`);
  return response.data;
};

export const previewInvoice = async (token: string, invoiceId: string) => {
  const doRequest = async (tkn: string) => {
    const response = await axios.get(`${baseUrl}/api/invoices/${invoiceId}/preview`, {
      headers: { Authorization: `Bearer ${tkn}` },
      responseType: 'text'
    });
    return response.data;
  };
  try {
    return await doRequest(token);
  } catch (error: any) {
    if (error?.response?.status === 401 && firebaseAuth.currentUser) {
      const fresh = await firebaseAuth.currentUser.getIdToken(true);
      return await doRequest(fresh);
    }
    throw error;
  }
};

export const downloadInvoice = async (token: string, invoiceId: string) => {
  const doRequest = async (tkn: string) => {
    const response = await axios.get(`${baseUrl}/api/invoices/${invoiceId}/download`, {
      headers: { Authorization: `Bearer ${tkn}` },
      responseType: 'blob'
    });
    return response.data;
  };
  try {
    return await doRequest(token);
  } catch (error: any) {
    if (error?.response?.status === 401 && firebaseAuth.currentUser) {
      const fresh = await firebaseAuth.currentUser.getIdToken(true);
      return await doRequest(fresh);
    }
    throw error;
  }
};

export const downloadInvoiceCsv = async (token: string, invoiceId: string) => {
  const doRequest = async (tkn: string) => {
    const response = await axios.get(
      `${baseUrl}/api/invoices/${invoiceId}/export/csv`,
      {
        headers: { Authorization: `Bearer ${tkn}` },
        responseType: "blob",
      },
    );
    return response.data;
  };
  try {
    return await doRequest(token);
  } catch (error: any) {
    if (error?.response?.status === 401 && firebaseAuth.currentUser) {
      const fresh = await firebaseAuth.currentUser.getIdToken(true);
      return await doRequest(fresh);
    }
    throw error;
  }
};

export interface InvoiceDeliveryOptions {
  issuanceMode: "logibud" | "accounting_provider";
  sendToCustomer: boolean;
  customerEmail?: string;
  sendToAccounting: boolean;
  accountingEmail?: string;
  saveCustomerEmail: boolean;
}

export const sendInvoice = async (
  token: string,
  invoiceId: string,
  delivery: InvoiceDeliveryOptions,
  workspaceId?: string,
) => {
  const response = await apiPost(
    token,
    `/api/invoices/${invoiceId}/send`,
    delivery,
    workspaceId,
  );
  return response.data;
};

export const resendInvoice = async (
  token: string,
  invoiceId: string,
  delivery: InvoiceDeliveryOptions,
  workspaceId?: string,
) => {
  const response = await apiPost(
    token,
    `/api/invoices/${invoiceId}/resend`,
    delivery,
    workspaceId,
  );
  return response.data;
};

export const registerInvoicePayment = async (token: string, invoiceId: string, transaction: any) => {
  const response = await apiPost(token, `/api/invoices/${invoiceId}/payment`, transaction);
  return response.data;
};

export const creditInvoice = async (token: string, invoiceId: string, transaction: any) => {
  const response = await apiPost(token, `/api/invoices/${invoiceId}/credit`, transaction);
  return response.data;
};

export const previewOrderInvoice = async (token: string, orderId: string) => {
  const response = await apiPost(token, `/api/orders/${orderId}/invoice/preview`);
  return response.data;
};

export const createOrderInvoice = async (token: string, orderId: string, reviewToken?: string) => {
  const response = await apiPost(token, `/api/orders/${orderId}/invoice`, reviewToken ? { reviewToken } : undefined);
  return response.data;
};

// Added function to create an API key
export const createApiKey = async (
  token: string,
  workspaceId: string,
  name: string,
  role: string,
) => {
  const response = await apiPost(
    token,
    `/api/workspaces/${workspaceId}/apikeys`,
    { name, role },
  );
  // The 'response' variable from apiPost already contains the data (or an error was thrown)
  // So we just need to check if it's truthy (i.e., not an error case that somehow wasn't thrown)
  if (response) { 
    return response; // response is already the data object e.g. { audience, client_id, ... }
  }
  // This case should ideally not be reached if apiPost throws errors correctly
  console.error("API key creation did not return valid data from apiPost.", response);
  return null; 
};

export interface McpTokenMetadata {
  token_hash: string;
  workspace_id: string;
  name: string | null;
  role: string | null;
  scopes?: string[];
  createdAt?: any;
  createdBy?: string | null;
  revokedAt?: any;
  lastUsedAt?: any;
}

export interface McpTokenCredentials extends McpTokenMetadata {
  mcp_token: string;
  client_id: string;
}

export const getMcpTokens = async (
  token: string,
  workspaceId: string,
): Promise<McpTokenMetadata[]> => {
  const response = await apiGet(token, `/api/workspaces/${workspaceId}/mcp-tokens`);
  return response?.data?.tokens ?? response?.tokens ?? [];
};

export const createMcpToken = async (
  token: string,
  workspaceId: string,
  name: string,
  role: string,
  scopes: string[],
): Promise<McpTokenCredentials | null> => {
  const response = await apiPost(
    token,
    `/api/workspaces/${workspaceId}/mcp-tokens`,
    { name, role, scopes },
  );
  return response?.data ?? response ?? null;
};

export const revokeMcpToken = async (
  token: string,
  workspaceId: string,
  tokenHash: string,
): Promise<McpTokenMetadata | null> => {
  const response = await apiPost(
    token,
    `/api/workspaces/${workspaceId}/mcp-tokens/${tokenHash}/revoke`,
  );
  return response?.data?.token ?? response?.token ?? null;
};

export interface McpOAuthAuthorizePayload {
  client_id: string;
  redirect_uri: string;
  response_type: "code";
  state?: string | null;
  code_challenge: string;
  code_challenge_method: "S256";
  scope?: string | null;
  resource?: string | null;
  name?: string | null;
}

export interface McpOAuthAuthorizeResponse {
  redirect_uri: string;
  code_expires_in: number;
  scopes: string[];
}

export const createMcpOAuthAuthorizationCode = async (
  token: string,
  workspaceId: string,
  payload: McpOAuthAuthorizePayload,
): Promise<McpOAuthAuthorizeResponse | null> => {
  const mcpOAuthBaseUrl = process.env.REACT_APP_MCP_OAUTH_API_URL || baseUrl;
  const response = await apiPost(
    token,
    `${mcpOAuthBaseUrl}/api/workspaces/${workspaceId}/mcp-oauth/authorization-code`,
    payload,
  );
  return response?.data ?? response ?? null;
};

export const createAdminMcpOAuthAuthorizationCode = async (
  token: string,
  payload: McpOAuthAuthorizePayload,
): Promise<McpOAuthAuthorizeResponse | null> => {
  const response = await apiPost(
    token,
    "/api/admin/v1/oauth/authorization-code",
    payload,
  );
  return response?.data ?? response ?? null;
};

export const apiPost = async (
  token: string,
  url: string,
  data: any = {},
  workspaceId?: string,
) => {
  const requestUrl = url.startsWith("http") ? url : `${baseUrl}${url}`;
  const doRequest = async (tkn: string) => {
    const headers: any = { Authorization: `Bearer ${tkn}` };
    if (workspaceId) headers["x-workspace-id"] = workspaceId;
    const response = await axios.post(requestUrl, data, { headers });
    return response.data;
  };
  try {
    return await doRequest(token);
  } catch (error: any) {
    if (error?.response?.status === 401 && firebaseAuth.currentUser) {
      const fresh = await firebaseAuth.currentUser.getIdToken(true);
      return await doRequest(fresh);
    }
    console.error(`API Error (POST ${url}):`, error);
    throw error;
  }
};

export const apiPut = async (token: string, url: string, data: any = {}, workspaceId?: string) => {
  const doRequest = async (tkn: string) => {
    const response = await axios.put(`${baseUrl}${url}`, data, {
      headers: {
        Authorization: `Bearer ${tkn}`,
        "X-Workspace-Id": workspaceId,
      },
    });
    return response.data;
  };
  try {
    return await doRequest(token);
  } catch (error: any) {
    if (error?.response?.status === 401 && firebaseAuth.currentUser) {
      const fresh = await firebaseAuth.currentUser.getIdToken(true);
      return await doRequest(fresh);
    }
    console.log(error);
    throw error;
  }
};

export const apiGet = async (token: string, url: string, workspaceId?: string) => {
  const doRequest = async (tkn: string) => {
    const response = await axios.get(`${baseUrl}${url}`, {
      headers: {
        Authorization: `Bearer ${tkn}`,
        "X-Workspace-Id": workspaceId,
      },
    });
    return response.data;
  };
  try {
    return await doRequest(token);
  } catch (error: any) {
    if (error?.response?.status === 401 && firebaseAuth.currentUser) {
      const fresh = await firebaseAuth.currentUser.getIdToken(true);
      return await doRequest(fresh);
    }
    console.log(error);
    throw error;
  }
};

export const apiGetBlob = async (
  token: string,
  url: string,
  workspaceId?: string,
) => {
  const doRequest = async (tkn: string) => {
    const response = await axios.get(`${baseUrl}${url}`, {
      headers: {
        Authorization: `Bearer ${tkn}`,
        "X-Workspace-Id": workspaceId,
      },
      responseType: "blob",
    });
    return response.data as Blob;
  };
  try {
    return await doRequest(token);
  } catch (error: any) {
    if (error?.response?.status === 401 && firebaseAuth.currentUser) {
      const fresh = await firebaseAuth.currentUser.getIdToken(true);
      return await doRequest(fresh);
    }
    console.error(`API Error (GET blob ${url}):`, error);
    throw error;
  }
};

export const apiDelete = async (token: string, url: string, workspaceId?: string) => {
  const doRequest = async (tkn: string) => {
    const headers: any = { Authorization: `Bearer ${tkn}` };
    if (workspaceId) headers["X-Workspace-Id"] = workspaceId;
    const response = await axios.delete(`${baseUrl}${url}`, { headers });
    return response.data;
  };
  try {
    return await doRequest(token);
  } catch (error: any) {
    if (error?.response?.status === 401 && firebaseAuth.currentUser) {
      const fresh = await firebaseAuth.currentUser.getIdToken(true);
      return await doRequest(fresh);
    }
    console.log(error);
    throw error;
  }
};

const configuredBaseUrl =
  process.env.REACT_APP_CLOUD_API_URL || "http://localhost:8080";
const baseUrl: string =
  typeof window !== "undefined" && window.location.protocol === "https:"
    ? configuredBaseUrl.replace(/^http:\/\//, "https://")
    : configuredBaseUrl;


export const discardInvoice = async (token: string, invoiceId: string) => {
  const response = await apiPost(token, `/api/invoices/${invoiceId}/discard`);
  return response.data;
};
